<template>
  <div class="h-full flex flex-col">

    <!-- Loading mailbox -->
    <div v-if="loadingMailbox" class="flex items-center justify-center h-full text-gray-400 text-sm">
      <font-awesome-icon :icon="['fas', 'circle-notch']" spin class="mr-2" />
      Loading…
    </div>

    <!-- Not configured -->
    <div v-else-if="!mailbox || mailbox.status === 'inactive'" class="flex items-center justify-center h-full">
      <div class="max-w-sm text-center bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
        <font-awesome-icon :icon="['fas', 'envelope']" class="text-gray-300 text-4xl mb-4" />
        <h3 class="text-base font-semibold text-gray-700 mb-2">Provider Messaging not configured</h3>
        <p class="text-sm text-gray-500">
          Your account has not been set up for provider messaging.
          Contact your administrator if you think this is a mistake.
        </p>
      </div>
    </div>

    <!-- Inbox -->
    <div v-else class="flex flex-1 overflow-hidden">

      <!-- Folder sidebar -->
      <div class="shrink-0 w-40 border-r border-gray-200 bg-white flex flex-col py-2">
        <button
          v-for="folder in folders"
          :key="folder.key"
          type="button"
          class="flex items-center gap-2 px-4 py-2.5 text-sm transition-colors text-left"
          :class="
            selectedFolder === folder.key
              ? 'bg-primary/10 text-primary font-medium'
              : 'text-gray-600 hover:bg-gray-50'
          "
          @click="selectFolder(folder.key)"
        >
          <font-awesome-icon :icon="folder.icon" class="w-4 shrink-0" />
          <span>
            {{ folder.label }}
            <span v-if="selectedFolder === folder.key && unreadCount > 0" class="font-normal opacity-70">
              ({{ unreadCount }})
            </span>
          </span>
        </button>
      </div>

      <!-- Message list -->
      <div class="w-72 shrink-0 border-r border-gray-200 bg-white flex flex-col overflow-hidden">
        <!-- List header + compose -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span class="text-sm font-semibold text-gray-900">
            {{ currentFolderLabel }}
          </span>
          <div class="flex items-center gap-2">
            <button
              class="btn-icon text-gray-400 hover:text-gray-700"
              :class="loadingMessages ? 'opacity-50 pointer-events-none' : ''"
              title="Refresh"
              @click="loadInbox"
            >
              <font-awesome-icon :icon="['fas', 'rotate-right']" :spin="loadingMessages" />
            </button>
            <button class="btn-primary text-xs" @click="openCompose">
              <font-awesome-icon :icon="['fas', 'pen-to-square']" class="mr-1" />
              Compose
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loadingMessages" class="text-sm text-gray-400 text-center py-6">
          <font-awesome-icon :icon="['fas', 'circle-notch']" spin class="mr-1" />
          Loading…
        </div>

        <!-- Error -->
        <div v-else-if="messagesError" class="text-sm text-red-500 text-center py-6 px-4">
          {{ messagesError }}
        </div>

        <!-- Empty -->
        <div v-else-if="!messages.length" class="text-sm text-gray-400 text-center py-6">
          No messages.
        </div>

        <!-- List -->
        <ul v-else class="flex-1 overflow-y-auto divide-y divide-gray-100">
          <li v-for="msg in messages" :key="msg.id">
            <button
              type="button"
              class="w-full text-left px-4 py-3 hover:bg-gray-50 focus:outline-none transition-colors border-l-2"
              :class="[
                selectedMessageId === msg.id ? 'bg-blue-50' : '',
                !msg.read ? 'border-l-primary' : 'border-l-transparent',
              ]"
              @click="selectMessage(msg)"
            >
              <div class="flex items-start justify-between gap-1">
                <span
                  class="text-xs truncate flex-1"
                  :class="!msg.read ? 'font-semibold text-gray-900' : 'text-gray-700'"
                >
                  {{ counterpartName(msg) }}
                </span>
                <div class="flex items-center gap-1.5 shrink-0 ml-1">
                  <font-awesome-icon
                    v-if="msg.has_attachments"
                    :icon="['fas', 'paperclip']"
                    class="text-gray-400 text-[10px]"
                    title="Has attachments"
                  />
                  <font-awesome-icon
                    v-if="msg.entity_id"
                    :icon="['fas', 'user']"
                    class="text-primary text-[10px]"
                    :title="msg.entity_name"
                  />
                  <span
                    class="text-xs"
                    :class="!msg.read ? 'font-semibold text-gray-700' : 'text-gray-400'"
                  >
                    {{ formatDate(msg.received) }}
                  </span>
                </div>
              </div>
              <p v-if="counterpartHasName(msg)" class="text-xs text-gray-500 truncate mt-0.5">
                {{ counterpartEmail(msg) }}
              </p>
              <p
                class="text-xs truncate mt-0.5"
                :class="!msg.read ? 'font-semibold text-gray-800' : 'text-gray-500'"
              >
                {{ msg.subject }}
              </p>
            </button>
          </li>
        </ul>
      </div>

      <!-- Message detail -->
      <div class="flex-1 overflow-y-auto bg-white flex flex-col">
        <div v-if="!selectedMessage" class="flex items-center justify-center h-full text-gray-400 text-sm">
          Select a message to read
        </div>

        <template v-else>
          <!-- Detail header -->
          <div class="px-6 py-4 border-b border-gray-200">
            <div class="flex items-start justify-between gap-3">
              <h2 class="text-base font-semibold text-gray-900">{{ selectedMessage.subject }}</h2>
              <div class="flex items-center gap-2 shrink-0">
                <button
                  v-if="selectedMessage.read"
                  type="button"
                  class="btn-icon text-gray-400 hover:text-gray-700"
                  title="Mark as unread"
                  @click="markUnread"
                >
                  <font-awesome-icon :icon="['fas', 'envelope']" />
                </button>
                <button
                  v-if="!loadingDetail"
                  type="button"
                  class="btn-secondary text-xs"
                  @click="openReply"
                >
                  <font-awesome-icon :icon="['fas', 'reply']" class="mr-1" />
                  Reply
                </button>
              </div>
            </div>
            <div class="mt-1 text-xs text-gray-500 flex flex-col gap-0.5">
              <span>
                <span class="font-medium text-gray-700">{{ selectedFolder === 'sent' ? 'To:' : 'From:' }}</span>
                {{ counterpartName(selectedMessage) }}
                <span v-if="counterpartHasName(selectedMessage)" class="text-gray-400">
                  &lt;{{ counterpartEmail(selectedMessage) }}&gt;
                </span>
              </span>
              <span>
                <span class="font-medium text-gray-700">Date:</span>
                {{ formatDateTime(selectedMessage.received) }}
              </span>
              <span v-if="selectedFolder === 'sent'" class="flex items-center gap-1.5">
                <span class="font-medium text-gray-700">Delivery Status:</span>
                <span v-if="loadingDetail" class="inline-block h-3.5 w-24 animate-pulse rounded-full bg-gray-200" />
                <span
                  v-else-if="fullMessage?.mdn_display"
                  class="flex items-center gap-1"
                  :class="MDN_COLOR[fullMessage.mdn_display.category]"
                >
                  <font-awesome-icon :icon="['fas', MDN_ICON[fullMessage.mdn_display.category]]" />
                  {{ fullMessage.mdn_display.label }}
                  <span v-if="fullMessage.mdn_display.detail" class="italic text-gray-400">
                    — {{ fullMessage.mdn_display.detail }}
                  </span>
                </span>
              </span>
              <span>
                <span class="font-medium text-gray-700">Patient:</span>
                <router-link
                  v-if="selectedMessage.entity_id"
                  :to="`/caremanagement/patients/${selectedMessage.entity_id}?tab=messages`"
                  class="ml-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary hover:bg-primary/20"
                >
                  <font-awesome-icon :icon="['fas', 'user']" class="text-[10px]" />
                  {{ selectedMessage.entity_name }}
                  <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="text-[10px]" />
                </router-link>
                <button
                  v-else
                  type="button"
                  class="ml-1 inline-flex items-center gap-1 text-xs text-gray-400 hover:text-primary"
                  @click="openLinkModal"
                >
                  <font-awesome-icon :icon="['fas', 'user-plus']" />
                  Link patient
                </button>
              </span>
              <span v-if="fullMessage?.attachments?.length">
                <span class="font-medium text-gray-700">Attachments:</span>
                <span class="ml-1 inline-flex flex-wrap gap-x-3 gap-y-0.5">
                  <a
                    v-for="(att, i) in fullMessage.attachments"
                    :key="i"
                    :href="`data:${att.mime_type};base64,${att.content}`"
                    :download="att.file_name"
                    class="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <font-awesome-icon :icon="['fas', 'paperclip']" />
                    {{ att.file_name }}
                  </a>
                </span>
              </span>
            </div>
          </div>

          <!-- Message body -->
          <div class="px-6 py-4 flex-1">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div
              v-if="fullMessage && fullMessage.html_message && fullMessage.html_message !== 'null'"
              class="prose prose-sm max-w-none text-gray-800"
              v-html="fullMessage.html_message"
            />
            <pre
              v-else-if="fullMessage && fullMessage.text_message && fullMessage.text_message !== 'null'"
              class="text-sm text-gray-800 whitespace-pre-wrap font-sans"
            >{{ fullMessage.text_message }}</pre>
            <div v-else-if="loadingDetail" class="text-sm text-gray-400">
              <font-awesome-icon :icon="['fas', 'circle-notch']" spin class="mr-1" />
              Loading…
            </div>
          </div>


        </template>
      </div>

    </div>

    <!-- ── Compose modal ──────────────────────────────────── -->
    <ComposeMessageModal
      v-model="showCompose"
      :mode="composeMode"
      :locked-patient="composeLockedPatient"
      :initial-to-email="composeToEmail"
      :initial-to-display-name="composeToDisplayName"
      :initial-subject="composeSubject"
      :initial-patient-id="composePatientId"
      :initial-patient-name="composePatientName"
      @sent="onComposeSent"
    />

    <!-- ── Link patient modal ──────────────────────────────────── -->
    <teleport to="body">
      <div
        v-if="showLinkModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="closeLinkModal"
      >
        <div class="bg-white rounded shadow-xl w-full max-w-md mx-4 flex flex-col max-h-[80vh]">
          <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <h3 class="font-semibold text-gray-900">Link Patient</h3>
            <button class="btn-icon" @click="closeLinkModal">
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>
          <div class="p-5 flex flex-col gap-3">
            <!-- Step 1: search -->
            <template v-if="!pendingLinkEntity">
              <p class="text-[11px] text-gray-500 -mt-1">
                Links this message to a patient record internally — not shared with the provider.
              </p>
              <div class="relative">
                <input
                  v-model="linkQuery"
                  type="text"
                  placeholder="Search by patient name…"
                  class="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                  autofocus
                  @input="onLinkQueryInput"
                />
                <div v-if="linkLoading" class="absolute right-3 top-2.5 text-gray-400 text-xs">
                  <font-awesome-icon :icon="['fas', 'circle-notch']" spin />
                </div>
              </div>
              <div v-if="!linkResults.length && !linkLoading && linkQuery.trim()" class="text-sm text-gray-400 text-center py-2">
                No patients found.
              </div>
              <ul v-if="linkResults.length" class="divide-y divide-gray-100 border border-gray-200 rounded max-h-60 overflow-y-auto">
                <li
                  v-for="entity in linkResults"
                  :key="entity.id"
                  class="px-3 py-2.5 text-sm hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                  @click="selectLinkEntity(entity)"
                >
                  <font-awesome-icon :icon="['fas', 'user']" class="text-gray-400 shrink-0" />
                  <span class="font-medium text-gray-800 flex-1">{{ entity.name }}</span>
                  <span v-if="entity.identifiers?.BENE_MBI_ID" class="text-gray-400 text-xs shrink-0">{{ entity.identifiers.BENE_MBI_ID }}</span>
                </li>
              </ul>
            </template>

            <!-- Step 2: confirm -->
            <template v-else>
              <div class="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 flex items-center gap-3">
                <font-awesome-icon :icon="['fas', 'user']" class="text-primary text-sm shrink-0" />
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-gray-900">{{ pendingLinkEntity.name }}</p>
                  <p v-if="pendingLinkEntity.identifiers?.BENE_MBI_ID" class="text-xs text-gray-500">{{ pendingLinkEntity.identifiers.BENE_MBI_ID }}</p>
                </div>
              </div>
              <p class="text-sm text-gray-600">Link this patient to the message?</p>
              <p v-if="linkError" class="text-sm text-red-600">{{ linkError }}</p>
              <div class="flex items-center gap-3 justify-end">
                <button
                  type="button"
                  class="btn-secondary text-xs"
                  :disabled="linkSaving"
                  @click="pendingLinkEntity = null; linkError = ''"
                >
                  Back
                </button>
                <button
                  type="button"
                  class="btn-primary text-xs"
                  :disabled="linkSaving"
                  @click="confirmLinkEntity"
                >
                  <font-awesome-icon v-if="linkSaving" :icon="['fas', 'circle-notch']" spin class="mr-1" />
                  {{ linkSaving ? 'Linking…' : 'Yes, link patient' }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </teleport>

    <ConfirmationModal
      :is-visible="showCloseConfirm"
      message="Discard this message without sending?"
      @confirm="showCloseConfirm = false; showCompose = false"
      @cancel="showCloseConfirm = false"
    />

  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapActions } from 'vuex';
import * as messagingApi from '@/services/api/providerMessaging.js';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import ComposeMessageModal from '@/components/shared/ComposeMessageModal.vue';
import { participantDisplayName, loadAddressBook, addressBookDisplayName } from '@/utils/providerMessaging.js';


export default {
  name: 'ProviderMessagingPage',
  components: { FontAwesomeIcon, ConfirmationModal, ComposeMessageModal },

  data() {
    return {
      loadingMailbox: false,
      mailbox: null,

      // Folder / message list
      selectedFolder: 'inbox',
      messages: [],
      loadingMessages: false,
      messagesError: null,

      // Selected message detail
      selectedMessageId: null,
      selectedMessage: null,
      fullMessage: null,
      loadingDetail: false,

      // Compose
      showCompose: false,
      composeMode: 'new',
      composeToEmail: '',
      composeToDisplayName: '',
      composeSubject: '',
      composePatientId: null,
      composePatientName: '',
      composeLockedPatient: null,

      showCloseConfirm: false,

      // Link patient modal
      showLinkModal: false,
      linkQuery: '',
      linkResults: [],
      linkLoading: false,
      linkDebounceTimer: null,
      pendingLinkEntity: null,
      linkSaving: false,
      linkError: '',

      folders: [],
      addressBook: null,

      MDN_ICON: { delivered: 'circle-check', failed: 'circle-xmark', pending: 'circle-question' },
      MDN_COLOR: { delivered: 'text-green-700', failed: 'text-red-600', pending: 'text-gray-500' },
    };
  },

  computed: {
    currentFolderLabel() {
      return this.folders.find((f) => f.key === this.selectedFolder)?.label ?? '';
    },

    unreadCount() {
      return this.messages.filter((m) => !m.read).length;
    },
  },

  async mounted() {
    const FOLDER_ICONS = {
      inbox: ['fas', 'inbox'],
      sent: ['fas', 'paper-plane'],

    };

    loadAddressBook().then((book) => { this.addressBook = book; });

    this.loadingMailbox = true;
    try {
      const [folders, mailbox] = await Promise.allSettled([
        messagingApi.getFolders(),
        messagingApi.getMailbox(),
      ]);

      if (folders.status === 'fulfilled') {
        this.folders = folders.value.map((f) => ({ ...f, icon: FOLDER_ICONS[f.key] || ['fas', 'folder'] }));
      }

      if (mailbox.status === 'fulfilled') {
        this.mailbox = mailbox.value;
      } else if (mailbox.reason?.response?.status !== 404) {
        console.error('[ProviderMessagingPage] failed to load mailbox:', messagingApi.errorMessage(mailbox.reason));
      }
    } finally {
      this.loadingMailbox = false;
    }

    if (this.mailbox?.status === 'active') {
      await this.loadInbox();
      const targetId = this.$route?.query?.messageId || null;
      if (targetId) await this.openMessageById(targetId);
    }
  },

  methods: {
    ...mapActions('auth', ['fetchUnreadMessageCount']),

    // ── Folder / messages ────────────────────────────────────────────────────

    async selectFolder(key) {
      this.selectedFolder = key;
      await this.loadInbox();
    },

    async loadInbox() {
      if (!this.mailbox) return;
      this.loadingMessages = true;
      this.selectedMessageId = null;
      this.selectedMessage = null;
      this.fullMessage = null;
      this.messagesError = null;
      try {
        this.messages = await messagingApi.listMessages({
          folder: this.selectedFolder,
        });
      } catch (e) {
        this.messagesError = messagingApi.errorMessage(e);
        console.error('[ProviderMessagingPage] failed to load inbox:', this.messagesError);
      } finally {
        this.loadingMessages = false;
      }
    },

    async openMessageById(targetId) {
      const match = (m) => String(m.id) === String(targetId);
      const inCurrent = this.messages.find(match);
      if (inCurrent) {
        await this.selectMessage(inCurrent);
        return;
      }
      for (const folder of this.folders) {
        if (folder.key === this.selectedFolder) continue;
        try {
          const others = await messagingApi.listMessages({ folder: folder.key });
          const found = others.find(match);
          if (found) {
            this.selectedFolder = folder.key;
            this.messages = others;
            await this.selectMessage(found);
            return;
          }
        } catch (_) { /* ignore */ }
      }
    },

    async selectMessage(msg) {
      this.selectedMessageId = msg.id;
      this.selectedMessage = msg;
      this.fullMessage = null;

      // Mark read
      if (!msg.read) {
        messagingApi.markMessage(msg.id, { read: true })
          .then(() => this.fetchUnreadMessageCount({ skipCache: true }))
          .catch(() => {});
        msg.read = true;
      }

      this.loadingDetail = true;
      try {
        this.fullMessage = await messagingApi.getMessage(msg.id);
      } catch (e) {
        console.error('[ProviderMessagingPage] failed to load message detail:', messagingApi.errorMessage(e));
      } finally {
        this.loadingDetail = false;
      }
    },

    markUnread() {
      const msg = this.selectedMessage;
      if (!msg) return;
      messagingApi.markMessage(msg.id, { read: false })
        .then(() => this.fetchUnreadMessageCount({ skipCache: true }))
        .catch(() => {});
      msg.read = false;
    },

    openReply() {
      this.composeMode = 'reply';
      this.composeToEmail = this.counterpartEmail(this.selectedMessage);
      this.composeToDisplayName = this.counterpartHasName(this.selectedMessage)
        ? this.counterpartName(this.selectedMessage)
        : '';
      const baseSubject = this.selectedMessage?.subject || '';
      this.composeSubject = baseSubject.startsWith('Re: ') ? baseSubject : `Re: ${baseSubject}`;
      const patientId = this.selectedMessage?.entity_id ?? null;
      const patientName = this.selectedMessage?.entity_name ?? '';
      this.composePatientId = patientId;
      this.composePatientName = patientName;
      this.composeLockedPatient = patientId ? { id: patientId, name: patientName } : null;
      this.showCompose = true;
    },

    counterpartName(msg) {
      const u = msg.folder === 'sent' ? msg.to_user : msg.from_user;
      if (!u) return '—';
      return participantDisplayName(u) || addressBookDisplayName(this.addressBook, u.direct_address) || u.direct_address || '—';
    },

    counterpartEmail(msg) {
      const u = msg.folder === 'sent' ? msg.to_user : msg.from_user;
      return u?.direct_address ?? '';
    },

    // True only when counterpartName resolved to a real name — used to avoid
    // showing the direct address a second time when it's already the "name".
    counterpartHasName(msg) {
      const u = msg.folder === 'sent' ? msg.to_user : msg.from_user;
      return Boolean(u && (participantDisplayName(u) || addressBookDisplayName(this.addressBook, u.direct_address)));
    },

    formatDate(value) {
      if (!value) return '—';
      const ms = typeof value === 'number' ? value : Date.parse(value);
      const d = new Date(ms);
      if (isNaN(d)) return '—';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },

    formatDateTime(value) {
      if (!value) return '—';
      const ms = typeof value === 'number' ? value : Date.parse(value);
      const d = new Date(ms);
      if (isNaN(d)) return '—';
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },

    // ── Compose ──────────────────────────────────────────────────────────────

    openCompose() {
      this.composeMode = 'new';
      this.composeToEmail = '';
      this.composeToDisplayName = '';
      this.composeSubject = '';
      this.composePatientId = null;
      this.composePatientName = '';
      this.composeLockedPatient = null;
      this.showCompose = true;
    },

    maybeCloseCompose() {
      this.showCloseConfirm = true;
    },

    async onComposeSent() {
      await this.loadInbox();
    },

    async fetchPatientSearch(query) {
      this.linkLoading = true;
      try {
        const { data } = await axios.get('/api/entities/', {
          params: { entity_type_name: 'Patient', name: query },
        });
        const raw = Array.isArray(data) ? data : (data.results ?? []);
        const seen = new Set();
        this.linkResults = raw.filter((e) => {
          const key = e.identifiers?.BENE_MBI_ID ?? `name:${e.name}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      } catch (e) {
        console.error('[ProviderMessagingPage] patient search failed:', e?.message);
      } finally {
        this.linkLoading = false;
      }
    },

    // ── Link patient modal ────────────────────────────────────────────────────

    openLinkModal() {
      this.linkQuery = '';
      this.linkResults = [];
      this.linkLoading = false;
      this.pendingLinkEntity = null;
      this.linkSaving = false;
      this.linkError = '';
      this.showLinkModal = true;
    },

    closeLinkModal() {
      this.showLinkModal = false;
    },

    onLinkQueryInput() {
      clearTimeout(this.linkDebounceTimer);
      const q = this.linkQuery.trim();
      if (!q) { this.linkResults = []; return; }
      this.linkDebounceTimer = setTimeout(() => this.fetchPatientSearch(q), 300);
    },

    selectLinkEntity(entity) {
      this.pendingLinkEntity = entity;
      this.linkError = '';
    },

    async confirmLinkEntity() {
      if (!this.pendingLinkEntity) return;
      this.linkSaving = true;
      this.linkError = '';
      try {
        const result = await messagingApi.linkMessageEntity(
          this.selectedMessage.id,
          { entity_id: this.pendingLinkEntity.id },
        );
        this.selectedMessage.entity_id = result.entity_id;
        this.selectedMessage.entity_name = result.entity_name;
        this.closeLinkModal();
      } catch (e) {
        this.linkError = messagingApi.errorMessage(e);
      } finally {
        this.linkSaving = false;
      }
    },

  },
};
</script>
